import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  Button,
  FileUploaderDropContainer,
  FileUploaderItem,
  Modal,
} from "@carbon/react";
import PageHeader from "../carbon/PageHeader/PageHeader";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import {
  useDownloadSuppliersTemplate,
  useUploadSupplierExcel,
} from "../../hooks/useSuppliers";
import { useNotification } from "../../context";
import "./newMassiveSuppliersForm.scss";

const XLSX_MIME_TYPE =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * The bulk-supplier-upload form (`POST /suppliers/upload/excel`), rebuilt on Carbon's
 * `FileUploaderDropContainer`/`FileUploaderItem` (Task 7, Suppliers flow) in place of 295 lines
 * of Tailwind and a hand-rolled `sr-only` dropzone. `labelText` below is deliberately
 * `t("suppliers.form.upload")` ("Upload File") — the SAME string the pre-Carbon `<label
 * htmlFor="massiveDeliveryFile">` used — so the accessible name the e2e bulk test drives via
 * `page.getByLabel("Upload File")` is unchanged: `FileUploaderDropContainer` renders `labelText`
 * both as its visible button text AND as a real `<label htmlFor={hiddenInputId}>` wrapping the
 * underlying `<input type="file">`, so `getByLabel` resolves to that input exactly as it did
 * before, and `setInputFiles` still works without the input needing to be visible.
 *
 * HONEST SUCCESS MESSAGING (the actual point of this task, not just the Carbon rebuild):
 * `POST /suppliers/upload/excel` (`SupplierController#uploadSuppliersExcel`,
 * `spectum/.../SupplierController.java:110-129`) is FIRE-AND-FORGET ASYNC — it starts
 * `uploadSuppliersFromExcelAsync(...)` and returns `{status: "processing", jobId, filename,
 * message}` immediately, before a single row is parsed. The pre-Carbon form fired
 * `t("suppliers.success")` — "The bulk suppliers file was uploaded successfully!" — off that same
 * response, which is false: nothing has been created yet. Per-row/aggregate outcomes only ever
 * reach a server log (`SupplierService#uploadSuppliersFromExcelAsync`, `:598-603`) — there is NO
 * job-status/result endpoint to poll (unlike Offers'/PR-import's own async imports, which DO
 * expose one), so this form genuinely cannot report what happened, only that the upload was
 * ACCEPTED. `t("suppliers.massive.processing")` says exactly that ("Upload started — suppliers
 * are being processed in the background.") instead of claiming success. A future backend
 * improvement — a status endpoint this form could poll, mirroring Offers'/PR-import's own pattern
 * — would let a later task replace this with the real per-row result; building that endpoint is
 * out of scope here.
 *
 * Everything else the pre-Carbon form did is preserved: the template download (blob +
 * Content-Disposition filename parsing), the `.xlsx` MIME-type check with the same error copy,
 * the confirm-before-upload step, cancel → `/suppliers`, and the three instruction steps.
 * HTTP-level failures (the upload request itself rejecting) still surface through the same error
 * modal as before — only the SUCCESS copy changes, because only the success case was dishonest.
 */
const NewMassiveSuppliersForm = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const navigate = useNavigate();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { mutate: downloadTemplate, isPending: isDownloading } =
    useDownloadSuppliersTemplate();

  const { mutate: uploadExcel, isPending: isUploading } =
    useUploadSupplierExcel(
      () => {
        openNotification(t("suppliers.massive.processing"));
        setConfirmOpen(false);
        navigate("/suppliers");
      },
      (error) => {
        setConfirmOpen(false);
        setErrorMessage(
          error.response?.data || t("suppliers.form.validations.uploadError")
        );
        setErrorModalOpen(true);
      }
    );

  const handleDownloadSample = () => {
    downloadTemplate(undefined, {
      onSuccess: (response) => {
        // Create a URL for the downloaded file
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        // Get filename from the Content-Disposition header or use a default
        const contentDisposition = response.headers["content-disposition"];
        let filename = "supplier_template.xlsx";

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
          );
          if (filenameMatch?.[1]) {
            filename = filenameMatch[1].replace(/['"]/g, "");
          }
        }

        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();

        // Clean up
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      },
      onError: () => {
        setErrorMessage(t("suppliers.form.validations.downloadError"));
        setErrorModalOpen(true);
      },
    });
  };

  const handleAddFiles = (
    _event: unknown,
    { addedFiles }: { addedFiles: File[] }
  ) => {
    const file = addedFiles[0];
    if (!file) return;

    if (file.type !== XLSX_MIME_TYPE) {
      setFileError(t("suppliers.form.validations.fileTypeError"));
      return;
    }

    setFileError("");
    setSelectedFile(file);
    setFileName(file.name);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileName(null);
    setFileError("");
  };

  const handleUploadClick = () => {
    if (!selectedFile) {
      setFileError(t("suppliers.form.validations.uploadError"));
      return;
    }
    setFileError("");
    setConfirmOpen(true);
  };

  const handleConfirmUpload = () => {
    if (!selectedFile) {
      setFileError(t("suppliers.form.validations.uploadError"));
      setConfirmOpen(false);
      return;
    }
    uploadExcel(selectedFile);
  };

  return (
    <div>
      <PageHeader
        title={t("suppliers.massive.new")}
        action={
          <Button onClick={handleDownloadSample} disabled={isDownloading}>
            {isDownloading
              ? t("suppliers.massive.downloading")
              : t("suppliers.massive.downloadSample")}
          </Button>
        }
      />

      <div className="new-massive-suppliers-form__instructions cds-form-surface">
        <p>{t("suppliers.massive.instructions")}</p>
        <ol>
          <li>{t("suppliers.massive.step1")}</li>
          <li>{t("suppliers.massive.step2")}</li>
          <li>{t("suppliers.massive.step3")}</li>
        </ol>
      </div>

      <div className="new-massive-suppliers-form__upload cds-form-surface">
        <FileUploaderDropContainer
          accept={[".xlsx"]}
          labelText={
            isUploading ? t("cta.uploading") : t("suppliers.form.upload")
          }
          onAddFiles={handleAddFiles}
          disabled={isUploading}
        />
        {fileName && (
          <FileUploaderItem
            name={fileName}
            status={isUploading ? "uploading" : "edit"}
            onDelete={handleRemoveFile}
          />
        )}
        {fileError && (
          <p className="new-massive-suppliers-form__error">{fileError}</p>
        )}

        <div className="new-massive-suppliers-form__actions">
          <Button kind="secondary" as={Link} to="/suppliers">
            {t("cta.cancel")}
          </Button>
          <Button
            onClick={handleUploadClick}
            disabled={isUploading || !fileName}
          >
            {isUploading ? t("cta.uploading") : t("cta.upload")}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={t("suppliers.massive.new")}
        body={t("suppliers.massive.addQuestion")}
        confirmLabel={t("cta.upload")}
        onConfirm={handleConfirmUpload}
        onCancel={() => setConfirmOpen(false)}
        isPending={isUploading}
      />

      <Modal
        open={errorModalOpen}
        modalHeading={t("suppliers.massive.error")}
        primaryButtonText={t("cta.cancel")}
        onRequestSubmit={() => setErrorModalOpen(false)}
        onRequestClose={() => setErrorModalOpen(false)}
      >
        <p>{errorMessage}</p>
      </Modal>
    </div>
  );
};

export default NewMassiveSuppliersForm;
